import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Filter, Sparkles, MapPin, Users, Calendar, Plus,
} from 'lucide-react';
import { fetchEvents, fetchRecommendations } from '../../api/move';
import type { MoveEvent, MoveLevel } from '../../api/move';
import { API_BASE } from '../../config';
import styles from '../../styles/Move.module.css';

const LEVELS: { key: MoveLevel | 'all'; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'iniciacion', label: 'Principiante' },
  { key: 'medio', label: 'Intermedio' },
  { key: 'avanzado', label: 'Avanzado' },
];

function formatDateChip(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: 'short' });
  } catch { return iso; }
}

function formatDateLong(iso: string) {
  try {
    return new Date(iso).toLocaleString('es-ES', {
      weekday: 'long', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function imageUrl(filename?: string | null) {
  if (!filename) return null;
  if (filename.startsWith('http')) return filename;
  return `${API_BASE}/api/move_images/${filename}`;
}

interface EventCardProps {
  event: MoveEvent;
  onClick: () => void;
}

function EventCardItem({ event, onClick }: EventCardProps) {
  const img = imageUrl(event.imagen);
  const ocupadas = event.plazas_ocupadas ?? 0;
  return (
    <div className={styles.eventCard} onClick={onClick}>
      <div className={styles.eventCardImg}>
        {img ? <img src={img} alt={event.titulo} /> : <div className={styles.eventImgFallback} />}
        <div className={styles.eventCardOverlay}>
          <span className={styles.eventCardDate}>
            <Calendar size={11} /> {formatDateChip(event.fecha_inicio)}
          </span>
        </div>
      </div>
      <div className={styles.eventCardBody}>
        <p className={styles.eventCardTitle}>{event.titulo}</p>
        <div className={styles.cardMeta}>
          <span className={`${styles.tag} ${styles.tagSky}`}>{event.nivel}</span>
          <span className={`${styles.tag} ${styles.tagViolet}`}>{event.tipo}</span>
          {event.ubicacion_nombre && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MapPin size={12} /> {event.ubicacion_nombre}
            </span>
          )}
          {event.plazas_max != null && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Users size={12} /> {ocupadas}/{event.plazas_max}
            </span>
          )}
        </div>
        <p className={styles.cardSubtitle} style={{ marginTop: 6 }}>
          {formatDateLong(event.fecha_inicio)}
        </p>
      </div>
    </div>
  );
}

export default function EventsListPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<MoveEvent[]>([]);
  const [recommendedIds, setRecommendedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState<MoveLevel | 'all'>('all');
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const filters: { nivel?: MoveLevel } = {};
        if (level !== 'all') filters.nivel = level;
        const [list, recs] = await Promise.all([
          fetchEvents(filters),
          fetchRecommendations().catch(() => ({ events: [], routes: [], nudge: null })),
        ]);
        setEvents(list);
        setRecommendedIds(new Set(recs.events.map(e => e.event_id)));
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Error cargando eventos');
      } finally {
        setLoading(false);
      }
    })();
  }, [level]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q ? events.filter(e => e.titulo.toLowerCase().includes(q)) : events;
    return [...base].sort(
      (a, b) => new Date(a.fecha_inicio).getTime() - new Date(b.fecha_inicio).getTime()
    );
  }, [events, query]);

  const recommended = filtered.filter(e => recommendedIds.has(e.event_id));
  const others = filtered.filter(e => !recommendedIds.has(e.event_id));

  return (
    <div className={`move-app ${styles.page}`}>
      <header className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroOrb} />
        <div className={styles.heroInner}>
          <p className={styles.greetingLabel}>Comunidad</p>
          <h1 className={styles.greetingName}>Eventos</h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, marginTop: 4 }}>
            {filtered.length} {filtered.length === 1 ? 'actividad' : 'actividades'} cerca
          </p>
          <button
            onClick={() => navigate('/move/events/new')}
            style={{
              marginTop: 14, background: 'rgba(255,255,255,0.18)', border: 0, color: '#fff',
              padding: '8px 16px', borderRadius: 999, fontSize: 13, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
            }}
          >
            <Plus size={15} /> Crear evento
          </button>
        </div>
      </header>

      <div style={{ marginTop: 16 }}>
        <div className={styles.searchWrap}>
          <Search size={18} className={styles.searchIcon} />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar eventos…"
            className={styles.searchInput}
          />
          <button className={styles.searchAction} aria-label="Filtros">
            <Filter size={16} />
          </button>
        </div>

        <div className={styles.pillRow} style={{ marginTop: 12 }}>
          {LEVELS.map(l => (
            <button
              key={l.key}
              className={`${styles.pill} ${level === l.key ? styles.pillActive : ''}`}
              onClick={() => setLevel(l.key)}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className={styles.empty}>Cargando…</div>}
      {error && <div className={styles.empty}>{error}</div>}

      {!loading && recommended.length > 0 && (
        <section style={{ marginTop: '1.25rem' }}>
          <div className={styles.subHeader}>
            <Sparkles size={14} className={styles.subHeaderIconViolet} />
            <h2 className={styles.subHeaderTitle}>Recomendados por IA</h2>
          </div>
          <div className={styles.section} style={{ paddingTop: 0 }}>
            <div className={styles.cardList}>
              {recommended.map(ev => (
                <EventCardItem
                  key={ev.event_id}
                  event={ev}
                  onClick={() => navigate(`/move/events/${ev.event_id}`)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      <section style={{ marginTop: '1.25rem' }}>
        <div className={styles.subHeader}>
          <h2 className={styles.subHeaderTitle}>Próximos eventos</h2>
        </div>
        <div className={styles.section} style={{ paddingTop: 0 }}>
          <div className={styles.cardList}>
            {others.map(ev => (
              <EventCardItem
                key={ev.event_id}
                event={ev}
                onClick={() => navigate(`/move/events/${ev.event_id}`)}
              />
            ))}
            {!loading && filtered.length === 0 && (
              <div className={styles.card} style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                <p style={{ fontFamily: 'Sora, Inter, sans-serif', fontWeight: 700, fontSize: 14, margin: 0 }}>
                  Sin eventos por ahora
                </p>
                <p style={{ fontSize: 12, color: 'var(--move-muted-fg)', marginTop: 4 }}>
                  Vuelve pronto o ajusta los filtros
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
